/**
 * Native-Android bottom-navigation semantics for React Router.
 *
 * Problem: the sidebar / bottom-nav tabs use <NavLink>, which always PUSHES a
 * history entry. So Dashboard → Inspection → Dashboard leaves a 3-deep stack and
 * the Android back button walks back through every tab you touched instead of
 * exiting from the home screen.
 *
 * Fix (mirrors Android's Navigation Component): the role's Dashboard is the
 * "start destination" and always stays at the BASE of the back stack. Any tab
 * you switch to sits at most ONE level above it, so the stack is only ever:
 *
 *     [home]            when you're on Dashboard
 *     [home, tab]       when you're on any other tab
 *     [home, tab, …]    plus whatever detail pages you drill into
 *
 * Detail-page navigation (row taps, "New", etc.) still pushes normally, so Back
 * inside a drill-down is untouched — we only collapse switches *between* tabs.
 *
 * How we pop precisely to home: React Router records the current position in the
 * session history at `window.history.state.idx`. We remember the index that home
 * occupies (useTrackHomeIndex) and, when returning to it, `navigate()` by the
 * exact negative delta — no fragile assumptions about what sits in between.
 */
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";

// History index (window.history.state.idx) at which the home/start destination
// currently lives. Module-scoped so it survives the mount/unmount churn of the
// nav drawer and bottom bar; there is only ever one home per app session.
let homeHistoryIdx: number | null = null;

function currentHistoryIdx(): number {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === "number" ? idx : 0;
}

/**
 * Always-mounted tracker (call once, from the Layout). Remembers where home sits
 * in the history stack so tab switches can pop straight back to it.
 */
export function useTrackHomeIndex(homePath: string): void {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === homePath) homeHistoryIdx = currentHistoryIdx();
  }, [location.pathname, homePath]);
}

/**
 * Returns `goTab(to)` — navigate to a top-level destination with the tab
 * semantics described above. Use it for every sidebar / bottom-nav item.
 */
export function useTabNavigate(homePath: string): (to: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to: string) => {
      // Already on this destination — don't add a redundant entry.
      if (location.pathname === to) return;
      navigateTopLevel(navigate, to, homePath);
    },
    [navigate, location.pathname, homePath],
  );
}

function navigateTopLevel(navigate: NavigateFunction, to: string, home: string): void {
  const curIdx = currentHistoryIdx();
  const homeIdx = homeHistoryIdx;

  if (to === home) {
    // Back to the start destination: if it's below us on the stack, pop exactly
    // to it so the tabs we passed through don't linger (Dashboard → Back exits).
    if (homeIdx !== null && curIdx > homeIdx) {
      navigate(homeIdx - curIdx); // negative delta = go back N entries
    } else {
      // Home isn't on the stack (e.g. deep-linked straight onto a tab) — just go.
      navigate(to);
    }
    return;
  }

  // Switching to a non-home tab. Target stack shape: [home, to].
  if (homeIdx === null || curIdx <= homeIdx) {
    // On (or below) home, or home not yet known: push so home stays underneath.
    navigate(to);
  } else if (curIdx === homeIdx + 1) {
    // Exactly one level above home → on another tab root. Replace it so we swap
    // tabs without growing the stack.
    navigate(to, { replace: true });
  } else {
    // Deeper than a tab root (inside a detail drill-down). Push normally so Back
    // still returns to that detail — we never collapse an in-progress flow.
    navigate(to);
  }
}
